import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Breadcrumbs,
    Container
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    TrendingUp,
    TrendingDown,
    AttachMoney,
    PendingActions,
    AccountBalance,
    Receipt
} from '@mui/icons-material';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';
import { formatDateForInput } from '../../utils/dateUtils';

/** revenueBreakdown sections sourced from Shop / F&B sales rather than transactions */
const POS_SALES_CATEGORIES = ['Pro Shop', 'Food & Beverage'];

interface FinancialReportData {
    revenue: {
        currentMonth: number;
        lastMonth: number;
        pendingRevenue: number;
        recurringMembershipRevenue: number;
    };
    transactions: {
        byType: Array<{ type: string; amount: number; count: number; percentage: number }>;
        totalCount: number;
    };
}

export const FinancialReport: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<FinancialReportData | null>(null);
    
    // Initialize date range to current month (mirrors accounting report)
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [prevPeriodLabel, setPrevPeriodLabel] = useState<string>('');

    useEffect(() => {
        loadReportData();
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            // Calculate previous period: same duration, ending the day before startDate
            const periodMs = endDate.getTime() - startDate.getTime();
            const prevEndDate = new Date(startDate.getTime() - 24 * 60 * 60 * 1000);
            const prevStartDate = new Date(prevEndDate.getTime() - periodMs);

            const prevStartDateStr = formatDateForInput(prevStartDate);
            const prevEndDateStr = formatDateForInput(prevEndDate);

            setPrevPeriodLabel(`${prevStartDateStr} – ${prevEndDateStr}`);

            console.log('[FinancialReport] Date ranges:');
            console.log('  Current period:', startDateStr, 'to', endDateStr);
            console.log('  Previous period:', prevStartDateStr, 'to', prevEndDateStr);

            // Fetch stats for current and previous periods in parallel
            const [currentStats, prevStats] = await Promise.all([
                reportAPI.getTransactionStats({ startDate: startDateStr, endDate: endDateStr }),
                reportAPI.getTransactionStats({ startDate: prevStartDateStr, endDate: prevEndDateStr })
            ]);

            // Helper: derive revenue and outstanding from revenueBreakdown the same way
            // the accounting report computes Total Credit - Total Debit and Total Outstanding.
            // This ensures both reports always show identical figures for the same date range.
            const calcFromBreakdown = (stats: any) => {
                // Liabilities (wallet credits, PT packs, F&B tips owed to staff) are not revenue —
                // excluded exactly as the Accounting report's TOTAL excludes them.
                const parentRows = (stats.revenueBreakdown || []).filter(
                    (item: any) => (item.level ?? (item.isSubcategory ? 1 : 0)) === 0 && !item.isLiability
                );
                // credit / debit are explicit on each row (collected / refunded); older derivation as a fallback
                const totalCredit = parentRows.reduce(
                    (sum: number, item: any) => sum + (item.credit ?? item.amount + Math.abs(item.refunds) + item.tax), 0
                );
                const totalDebit = parentRows.reduce(
                    (sum: number, item: any) => sum + (item.debit ?? item.refunds), 0
                );
                const totalOutstanding = parentRows.reduce(
                    (sum: number, item: any) => sum + item.outstanding, 0
                );
                return {
                    revenue: totalCredit - totalDebit,
                    pendingRevenue: totalOutstanding
                };
            };

            const current = calcFromBreakdown(currentStats);
            const prev = calcFromBreakdown(prevStats);

            console.log('[FinancialReport] Current period revenue (Credit - Debit):', current.revenue);
            console.log('[FinancialReport] Previous period revenue (Credit - Debit):', prev.revenue);

            const reportData: FinancialReportData = {
                revenue: {
                    currentMonth: current.revenue,
                    lastMonth: prev.revenue,
                    pendingRevenue: current.pendingRevenue,
                    recurringMembershipRevenue: currentStats.recurringMembershipRevenue || 0
                },
                transactions: {
                    byType: [
                        ...(currentStats.transactionsByType || []).map((item: any) => ({
                            type: item.type,
                            amount: item.totalAmount,
                            count: item.count
                        })),
                        // Shop / F&B sales (all payment methods) come from the sales, not transactions
                        ...(currentStats.revenueBreakdown || [])
                            .filter((item: any) => POS_SALES_CATEGORIES.includes(item.category) && (item.level ?? 0) === 0)
                            .map((item: any) => ({
                                type: item.category,
                                amount: item.net ?? item.amount + item.tax,
                                count: item.category === 'Pro Shop'
                                    ? currentStats.posSales?.shop?.orderCount ?? 0
                                    : currentStats.posSales?.fnb?.tabCount ?? 0
                            }))
                    ].map((item: { type: string; amount: number; count: number }) => ({
                        ...item,
                        percentage: current.revenue > 0 ? (item.amount / current.revenue) * 100 : 0
                    })),
                    totalCount: (currentStats.transactionsByType || []).reduce(
                        (sum: number, item: any) => sum + item.count,
                        0
                    )
                }
            };

            setReportData(reportData);
        } catch (err) {
            setError('Failed to load financial report data');
            console.error('Error loading financial report:', err);
        } finally {
            setLoading(false);
        }
    };

    const calculateMoMChange = () => {
        if (!reportData) return { change: 0, percentage: 0, isPositive: true };

        const currentMonth = reportData.revenue.currentMonth;
        const lastMonth = reportData.revenue.lastMonth || 0;
        const change = currentMonth - lastMonth;
        const percentage = lastMonth > 0 ? ((change / lastMonth) * 100) : 0;

        return {
            change,
            percentage,
            isPositive: change >= 0
        };
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercentage = (value: number): string => {
        return `${value.toFixed(1)}%`;
    };

    const handleTransactionTypeClick = (type: string) => {
        // Shop / F&B slices have their own sales reports
        if (type === 'Pro Shop') { navigate('/shop-financial-report'); return; }
        if (type === 'Food & Beverage') { navigate('/fnb-financial-report'); return; }
        // Navigate to transaction management with type filter
        navigate('/transactions', { 
            state: { 
                filterType: type 
            } 
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!reportData) {
        return null;
    }

    const momChange = calculateMoMChange();

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Financial Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4">Financial Report</Typography>
                    <Box display="flex" gap={2} alignItems="center">
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => newValue && setStartDate(newValue)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { width: 160 }
                                }
                            }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => newValue && setEndDate(newValue)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { width: 160 }
                                }
                            }}
                        />
                    </Box>
                </Box>

            {/* Key Metrics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Revenue This Month */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <AttachMoney sx={{ mr: 1, color: 'success.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Revenue
                                </Typography>
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(reportData.revenue.currentMonth)}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                Completed transactions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Pending Revenue */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <PendingActions sx={{ mr: 1, color: 'warning.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Pending Revenue
                                </Typography>
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(reportData.revenue.pendingRevenue)}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                Pending 
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Month over Month Change */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                {momChange.isPositive ? (
                                    <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                                ) : (
                                    <TrendingDown sx={{ mr: 1, color: 'error.main' }} />
                                )}
                                <Typography color="textSecondary" variant="body2">
                                    Period over Period
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography
                                    variant="h4"
                                    component="div"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: momChange.isPositive ? 'success.main' : 'error.main'
                                    }}
                                >
                                    {momChange.isPositive ? '+' : ''}{formatCurrency(momChange.change)}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <Chip
                                    label={`${momChange.isPositive ? '+' : ''}${formatPercentage(momChange.percentage)}`}
                                    size="small"
                                    color={momChange.isPositive ? 'success' : 'error'}
                                    sx={{ fontWeight: 'bold' }}
                                />
                               
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recurring Membership Revenue */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <AccountBalance sx={{ mr: 1, color: 'info.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Recurring Revenue
                                </Typography>
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(reportData.revenue.recurringMembershipRevenue)}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                Forecasted (before taxes)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3}>
                {/* Transaction Breakdown by Type */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Transaction Breakdown by Type
                        </Typography>
                        {reportData.transactions.byType.length > 0 ? (
                            <>
                                <ReportChart
                                    type="pie"
                                    data={{
                                        labels: reportData.transactions.byType.map(item => 
                                            `${item.type}: ${formatCurrency(item.amount)} (${formatPercentage(item.percentage)})`
                                        ),
                                        datasets: [{
                                            data: reportData.transactions.byType.map(item => item.amount),
                                            backgroundColor: [
                                                '#4CAF50',
                                                '#2196F3',
                                                '#FF9800',
                                                '#F44336',
                                                '#9C27B0',
                                                '#00BCD4',
                                                '#FFEB3B',
                                                '#795548'
                                            ]
                                        }]
                                    }}
                                    height={300}
                                    options={{
                                        onHover: (event: any, activeElements: any) => {
                                            const canvas = event.native?.target;
                                            if (canvas) {
                                                canvas.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
                                            }
                                        },
                                        onClick: (event: any, elements: any) => {
                                            if (elements && elements.length > 0) {
                                                const index = elements[0].index;
                                                const type = reportData.transactions.byType[index].type;
                                                handleTransactionTypeClick(type);
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'right'
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: function(context: any) {
                                                        const item = reportData.transactions.byType[context.dataIndex];
                                                        return [
                                                            `${item.type}`,
                                                            `Amount: ${formatCurrency(item.amount)}`,
                                                            `Count: ${item.count} transactions`,
                                                            `Percentage: ${formatPercentage(item.percentage)}`
                                                        ];
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </>
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No transaction data available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Transaction Count by Type */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Transaction Volume by Type
                        </Typography>
                        {reportData.transactions.byType.length > 0 ? (
                            <ReportChart
                                type="bar"
                                data={{
                                    labels: reportData.transactions.byType.map(item => item.type),
                                    datasets: [{
                                        label: 'Transaction Count',
                                        data: reportData.transactions.byType.map(item => item.count),
                                        backgroundColor: '#2196F3',
                                        borderColor: '#1976D2',
                                        borderWidth: 1
                                    }]
                                }}
                                height={300}
                                options={{
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            ticks: {
                                                stepSize: 1
                                            }
                                        }
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                label: function(context: any) {
                                                    const item = reportData.transactions.byType[context.dataIndex];
                                                    return [
                                                        `Count: ${item.count} transactions`,
                                                        `Total: ${formatCurrency(item.amount)}`,
                                                        `Average: ${formatCurrency(item.count > 0 ? item.amount / item.count : 0)}`
                                                    ];
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No transaction data available
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            </Box>
        </LocalizationProvider>
        </Container>
    );
};
